import React, { useState } from 'react';
import { Search, Filter, Plus, Edit, Trash2, Shield, Users, Briefcase, Eye, Ban, CheckCircle } from 'lucide-react';

const UserManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const users = [
    {
      id: '1',
      name: 'John Doe',
      email: 'john.doe@email.com',
      userType: 'jobseeker',
      status: 'active',
      joinDate: '2024-12-15',
      lastActive: '2025-01-14',
      testsCompleted: 8,
      badge: 'gold',
      avatar: 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=150'
    },
    {
      id: '2',
      name: 'Sarah Johnson',
      email: 'sarah.j@techcorp.com',
      userType: 'hr',
      status: 'active',
      company: 'TechCorp Inc.',
      joinDate: '2024-11-20',
      lastActive: '2025-01-14',
      jobsPosted: 12,
      avatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150'
    },
    {
      id: '3',
      name: 'Michael Chen',
      email: 'michael.chen@email.com',
      userType: 'jobseeker',
      status: 'inactive',
      joinDate: '2024-10-05',
      lastActive: '2024-12-20',
      testsCompleted: 3,
      badge: 'silver',
      avatar: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=150'
    },
    {
      id: '4',
      name: 'Emily Rodriguez',
      email: 'emily.r@startupxyz.com',
      userType: 'hr',
      status: 'active',
      company: 'StartupXYZ',
      joinDate: '2024-09-12',
      lastActive: '2025-01-13',
      jobsPosted: 8,
      avatar: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=150'
    },
    {
      id: '5',
      name: 'Admin User',
      email: 'admin@vetriaai.com',
      userType: 'admin',
      status: 'active',
      joinDate: '2024-01-01',
      lastActive: '2025-01-14',
      avatar: 'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=150'
    }
  ];

  const getUserTypeIcon = (userType: string) => {
    switch (userType) {
      case 'jobseeker':
        return <Users className="w-4 h-4" />;
      case 'hr':
        return <Briefcase className="w-4 h-4" />;
      case 'admin':
        return <Shield className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  const getUserTypeBadge = (userType: string) => {
    const colors = {
      jobseeker: 'bg-blue-100 text-blue-800',
      hr: 'bg-green-100 text-green-800',
      admin: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[userType as keyof typeof colors]}`}>
        {getUserTypeIcon(userType)}
        <span className="ml-1 capitalize">{userType}</span>
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Active
          </span>
        );
      case 'inactive':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <Ban className="w-3 h-3 mr-1" />
            Inactive
          </span>
        );
      case 'suspended':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <Ban className="w-3 h-3 mr-1" />
            Suspended
          </span>
        );
      default:
        return null;
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (user.company && user.company.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = filterType === 'all' || user.userType === filterType;
    
    return matchesSearch && matchesFilter;
  });

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card-gradient flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-1">Manage all platform users and their permissions</p>
        </div>
        <button className="btn-danger">
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      {/* Filters */}
      <div className="glass-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-glass pl-10 pr-4 w-full"
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="input-glass px-3 py-2"
              >
                <option value="all">All Users</option>
                <option value="jobseeker">Job Seekers</option>
                <option value="hr">HR Users</option>
                <option value="admin">Admins</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="glass-card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-transparent backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <input
                type="checkbox"
                checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                onChange={handleSelectAll}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700 font-medium">
                {selectedUsers.length > 0
                  ? `${selectedUsers.length} selected`
                  : `${filteredUsers.length} users`
                }
              </span>
            </div>

            {selectedUsers.length > 0 && (
              <div className="flex space-x-2">
                <button className="px-3 py-1.5 bg-green-100/80 text-green-800 text-sm rounded-button hover:bg-green-200 transition-all backdrop-blur-sm font-medium">
                  Activate
                </button>
                <button className="px-3 py-1.5 bg-yellow-100/80 text-yellow-800 text-sm rounded-button hover:bg-yellow-200 transition-all backdrop-blur-sm font-medium">
                  Suspend
                </button>
                <button className="px-3 py-1.5 bg-red-100/80 text-red-800 text-sm rounded-button hover:bg-red-200 transition-all backdrop-blur-sm font-medium">
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200/50">
            <thead className="bg-gradient-to-r from-gray-50/80 to-gray-100/50 backdrop-blur-sm">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white/50 backdrop-blur-sm divide-y divide-gray-200/50">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-white/80 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleSelectUser(user.id)}
                        className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded mr-4"
                      />
                      <div className="flex items-center">
                        <img
                          src={user.avatar}
                          alt={user.name}
                          className="h-12 w-12 rounded-full ring-2 ring-white shadow-sm"
                        />
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-600">{user.email}</div>
                          {user.company && (
                            <div className="text-xs text-gray-500">{user.company}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getUserTypeBadge(user.userType)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(user.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>
                      <div className="text-sm text-gray-900">
                        Last active: {user.lastActive}
                      </div>
                      {user.userType === 'jobseeker' && (
                        <div className="text-xs text-gray-500">
                          {user.testsCompleted} tests completed
                          {user.badge && (
                            <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                              user.badge === 'gold' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {user.badge} badge
                            </span>
                          )}
                        </div>
                      )}
                      {user.userType === 'hr' && (
                        <div className="text-xs text-gray-500">
                          {user.jobsPosted} jobs posted
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.joinDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button className="text-blue-600 hover:text-blue-900 p-1">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="text-green-600 hover:text-green-900 p-1">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="text-red-600 hover:text-red-900 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12 bg-white/50 backdrop-blur-sm">
            <div className="text-gray-600 font-medium">No users found matching your criteria.</div>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid md:grid-cols-4 gap-6">
        <div className="stat-card text-center">
          <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">2,234</div>
          <div className="text-sm text-gray-700 font-semibold mt-2">Job Seekers</div>
        </div>
        <div className="stat-card text-center">
          <div className="text-4xl font-bold bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent">456</div>
          <div className="text-sm text-gray-700 font-semibold mt-2">HR Users</div>
        </div>
        <div className="stat-card text-center">
          <div className="text-4xl font-bold bg-gradient-to-r from-red-600 to-red-500 bg-clip-text text-transparent">12</div>
          <div className="text-sm text-gray-700 font-semibold mt-2">Admins</div>
        </div>
        <div className="stat-card text-center">
          <div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-purple-500 bg-clip-text text-transparent">2,702</div>
          <div className="text-sm text-gray-700 font-semibold mt-2">Total Users</div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;